import { useAtom } from '@reatom/npm-react'
import { memo } from 'react'
import styled, { css } from 'styled-components'

import { CountMax3DigitsFormatted } from '@root/shared/core/format'
import { useOptionalAtom } from '@root/shared/lib'
import { useTranslation } from '@root/shared/localization'
import { mediaMaxDesktopSmall, UnderlineTabs } from '@root/shared/ui-kit'

import {
  tradeTableTabAtom,
  tradesTableViewOptionAtom,
} from '../../atoms/reatom-trading-tables'
import { TradesTableViewOption } from '../../model/trades-table-view-option'

import type { TradeTableTab } from '../../atoms/reatom-trading-tables'
import type { AsyncStateN } from '@blade/std-lib'
import type { PairAddress, PairModel } from '@root/entities/pair'

type Props = {
  pairAddress: PairAddress
  pair: AsyncStateN.AsyncStateN<unknown, PairModel>
}

export const TradeTablesTabs = memo(({ pair: pairState }: Props) => {
  const { t } = useTranslation()

  const pair = pairState.value

  const [tab, setTab] = useAtom(tradeTableTabAtom)
  const [tradesTableViewOption] = useAtom(tradesTableViewOptionAtom)
  const isTradePanelOpen = tradesTableViewOption === TradesTableViewOption.Chart

  const holdersCount = useOptionalAtom(pair?.holdersCount)
  const devTokensCount = useOptionalAtom(pair?.creatorStats)?.totalPools

  const renderHoldersCount = () => {
    if (holdersCount === undefined) {
      return undefined
    }

    return (
      <>
        (<CountMax3DigitsFormatted count={holdersCount} />)
      </>
    )
  }

  const renderDevTokensCount = () => {
    if (devTokensCount === undefined) {
      return undefined
    }
    const count = Math.max(devTokensCount - 1, 0)

    return (
      <>
        (<CountMax3DigitsFormatted count={count} />)
      </>
    )
  }

  return (
    <Tabs value={tab} onChange={setTab} $tradesTabHidden={isTradePanelOpen}>
      {isTradePanelOpen ? undefined : (
        <UnderlineTabs.Tab value={'trades' satisfies TradeTableTab}>
          <TabLabel>{t('trade_other')}</TabLabel>
        </UnderlineTabs.Tab>
      )}
      <UnderlineTabs.Tab value={'history' satisfies TradeTableTab}>
        <TabLabel>{t('trade.my-trades')}</TabLabel>
      </UnderlineTabs.Tab>
      <UnderlineTabs.Tab value={'orders' satisfies TradeTableTab}>
        <TabLabel>{t('order_other')}</TabLabel>
      </UnderlineTabs.Tab>
      <UnderlineTabs.Tab value={'positions' satisfies TradeTableTab}>
        <TabLabel>{t('position_other')}</TabLabel>
      </UnderlineTabs.Tab>
      <UnderlineTabs.Tab value={'holders' satisfies TradeTableTab}>
        <TabLabel>
          <span>{t('holder_other')}</span>
          <span> {renderHoldersCount()}</span>
        </TabLabel>
      </UnderlineTabs.Tab>
      <UnderlineTabs.Tab value={'top-holders' satisfies TradeTableTab}>
        <TabLabel>{t('top-trader_other')}</TabLabel>
      </UnderlineTabs.Tab>
      <UnderlineTabs.Tab value={'dev-tokens' satisfies TradeTableTab}>
        <TabLabel>
          <span>{t('dev-tokens')}</span>
          <span> {renderDevTokensCount()}</span>
        </TabLabel>
      </UnderlineTabs.Tab>
    </Tabs>
  )
})

const Tabs = styled(UnderlineTabs<TradeTableTab>).attrs<{
  $tradesTabHidden: boolean
}>(({ $tradesTabHidden }) => ({
  style: {
    gridTemplateColumns: $tradesTabHidden
      ? 'repeat(5, auto) 1fr'
      : 'repeat(6, auto) 1fr',
  },
}))`
  --tab-font-size: 12px;
  --tab-padding-inline: 0;
  --tab-padding-inline: 15px;

  justify-content: start;
  padding-bottom: 1px;
  border-bottom: none;

  ${mediaMaxDesktopSmall(css`
    --tab-padding-inline: 4px;
    --tab-font-size: 11px;
    --tab-height: 28px;
  `)}
`
const TabLabel = styled.span`
  white-space: nowrap;
`
